import { useThrottledValue } from '@/hooks/useThrottledValue';
import { useTradeCoin } from '@/hooks/useTradeCoin';
import { usePairs } from '@/store/pairs';
import { usePrice } from '@/store/price';
import { useTradeStore } from '@/store/trade-store';
import { TradeData } from '@/types/trade';
import {
    calculateEarn,
    calculateLiquidationPrice,
    convertBigNumberToNumber,
} from '@/utils/formula';
import { numberFormatBreak } from '@/utils/numberFormat';
import BigNumber from 'bignumber.js';
import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';

// ─── Constants ────────────────────────────────────────────────────────────────

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function percentage(val: number): string {
    const formatted = numberFormatBreak(val, 2, 1);
    return val > 0 ? `+${formatted}%` : `${formatted}%`;
}

/**
 * Clamps PnL and earn so losses never exceed 100% of collateral.
 */
function clampPnl(
    earn: number,
    pnl: number,
    initialCollateral: number
): { earn: number; pnl: number } {
    if (pnl < -100) {
        return { earn: -initialCollateral, pnl: -100 };
    }
    return { earn, pnl };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useGetDataPosition() {
    const prices = usePrice(useShallow((state) => state.data));
    const price = useThrottledValue(prices, 1000);

    const openTrade = useTradeStore(
        useShallow((state) => state.orderTrade.openTrade)
    );

    const pairsInfo = usePairs(useShallow((state) => state.data));
    const stableCoin = useTradeCoin();

    const dataRow = useMemo(() => {
        return openTrade
            .map((x) => {
                // ── Resolve pair metadata ──────────────────────────────────────────
                const currentToken = pairsInfo?.find(
                    (y) =>
                        Number(x.trade_info.pair_index) === Number(y.pairIndex)
                );

                const currentPrice =
                    price?.[currentToken?.indexPrice ?? 0]?.price ?? 0;

                // ── Shared BigNumber values (avoid repeating fallback chain) ───────
                // NOTE: open_price takes precedence; max_price/min_price are fallbacks
                // for limit orders that haven't been filled yet.
                const rawOpenPrice =
                    x.trade_info.open_price ?? x.max_price ?? x.min_price ?? 0;
                const openPriceBN = new BigNumber(rawOpenPrice);

                // NOTE: initial_pos_token is preferred; position_size_usd is a legacy
                // fallback used by older contract versions.
                const rawCollateral =
                    x.trade_info.initial_pos_token ??
                    x.trade_info.position_size_usd ??
                    0;
                const collateralBN = new BigNumber(rawCollateral);
                const leverageBN = new BigNumber(x.trade_info.leverage ?? 0);

                // ── Earn / PnL at current price ────────────────────────────────────
                const rawEarn = calculateEarn({
                    isLong: x.trade_info.buy,
                    currenPrice: Number(currentPrice),
                    openPrice: openPriceBN,
                    leverage: leverageBN,
                    collateral: collateralBN,
                });

                const initialCollateralNumber = convertBigNumberToNumber(
                    collateralBN,
                    18
                );

                const rawPnl = (rawEarn * 100) / initialCollateralNumber;
                const { earn, pnl } = clampPnl(
                    rawEarn,
                    rawPnl,
                    initialCollateralNumber
                );

                // ── Earn at take-profit price ──────────────────────────────────────
                const earnBuyTp = calculateEarn({
                    isLong: x.trade_info.buy,
                    currenPrice: convertBigNumberToNumber(
                        new BigNumber(x.trade_info.tp),
                        10
                    ),
                    openPrice: openPriceBN,
                    leverage: leverageBN,
                    collateral: collateralBN,
                });

                // ── Derived values ─────────────────────────────────────────────────
                const collateralValue = collateralBN.dividedBy(1e18);

                // NOTE: calculateLiquidationPrice intentionally uses position_size_usd
                // (gross size) rather than initial_pos_token (net collateral after fees)
                // because the liquidation engine uses the gross value on-chain.
                const { liquidationPrice } = calculateLiquidationPrice({
                    isLong: x.trade_info.buy,
                    fee: 0,
                    collateral: new BigNumber(
                        x.trade_info.position_size_usd ?? 0
                    ),
                    openPrice: openPriceBN,
                    leverage: leverageBN,
                    sl: new BigNumber(x.trade_info.sl ?? 0),
                    decimalsToShow: 10,
                    decimalTokenUsdc: stableCoin.decimals,
                });

                const row: TradeData = {
                    type: x.trade_info.buy ? 'Long' : 'Short',
                    buy: x.trade_info.buy,
                    isLoading: !currentPrice,
                    pair: Number(x.trade_info.pair_index),
                    pairName: currentToken?.nameDisplay ?? '',
                    collateral: Math.abs(initialCollateralNumber),
                    positionSize: String(x.trade_info.position_size_usd),
                    collateralValue: collateralValue.toNumber(),
                    openFeeValue: convertBigNumberToNumber(
                        new BigNumber(
                            x.collateral_before_fee -
                                x.trade_info.initial_pos_token
                        ),
                        18
                    ),
                    sizeValue: collateralValue
                        .multipliedBy(x.trade_info.leverage)
                        .toNumber(),
                    // TradeData.open_price is string — convert number back to string
                    open_price: openPriceBN
                        .dividedBy(new BigNumber(10).pow(10))
                        .toFixed(),
                    take_profit: convertBigNumberToNumber(
                        new BigNumber(x.trade_info.tp),
                        10
                    ),
                    // TradeData.liq_sl is never — field is intentionally omitted (not used downstream)
                    liq_sl: undefined as never,
                    net_pnl: { earn: earn || 0, pnl },
                    earnBuyTp,
                    price: Number(currentPrice),
                    leverage: Number(x.trade_info.leverage),
                    index: x.trade_info.index,
                    liquidationPrice,
                    decimalToShow: currentToken?.decimalsToShow ?? 2,
                    imgUrl: currentToken?.imageUrl,
                    size:
                        Math.abs(initialCollateralNumber) *
                        Number(x.trade_info.leverage),
                    openFeeP: x.open_fee.toString(),
                    createdAt: x.created_at.seconds,
                    indexPrice: currentToken?.indexPrice ?? 0,
                    initialPosToken: String(rawCollateral),
                    group: currentToken?.group ?? 0,
                    tradeCoinName: stableCoin.symbol,
                    decimalTradeCoin: stableCoin.decimals,
                    lastUpdatedDate: x.lastUpdatedDate,
                };

                return row;
            })
            .sort((a, b) => a.type.localeCompare(b.type));
    }, [openTrade, pairsInfo, price, stableCoin]);

    return {
        data: dataRow as TradeData[],
        isEmpty: dataRow.length === 0,
    };
}
